import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { useOnboarding } from '../context/OnboardingContext';
import { OnboardingSubStepId } from '../types';
import { BRAND_COLORS } from '../../Pleri/components/ToolExecution/types';
import { PleriLogo } from '../../Pleri/components/PleriLogo';
import { updateUserMemory } from '../api';
import { useQueryClient } from '@tanstack/react-query';
import { useMemory, useUpdateMemory } from '../../../api/memory';
import { useUserProfile } from '../../../api/profile';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

// Saved into user memory as this English text and matched on later, so it
// stays as written here; screens render it through roleLabel / detailLevelText.
export const ROLE_OPTIONS = [
  { id: 'security-engineer', label: 'Security engineer' },
  { id: 'security-leader-manager', label: 'Security leader or manager' },
  { id: 'security-analyst', label: 'Security analyst' },
  { id: 'grc-compliance', label: 'GRC / compliance' },
  { id: 'devops-platform-engineer', label: 'DevOps or platform engineer' },
  { id: 'software-engineer', label: 'Software engineer' },
  { id: 'cto-engineering-leader', label: 'CTO or engineering leader' },
  { id: 'something-else', label: 'Something else' },
];

export const DETAIL_LEVEL_OPTIONS = [
  {
    id: 'brief',
    label: 'Brief',
    description: 'Short answers, just what you need.',
  },
  {
    id: 'standard',
    label: 'Standard',
    description: 'Some detail, but still concise.',
  },
  {
    id: 'in-depth',
    label: 'In depth',
    description: 'More detail and explanation.',
  },
];

const ROLE_LABEL_KEYS: Record<string, string> = {
  'security-engineer': 'securityEngineer',
  'security-leader-manager': 'securityLeaderOrManager',
  'security-analyst': 'securityAnalyst',
  'grc-compliance': 'grcCompliance',
  'devops-platform-engineer': 'devOpsOrPlatformEngineer',
  'software-engineer': 'softwareEngineer',
  'cto-engineering-leader': 'ctoOrEngineeringLeader',
  'something-else': 'somethingElse',
};

export const roleLabel = (
  t: TFunction<'pleri/onboarding'>,
  id: string,
): string => (ROLE_LABEL_KEYS[id] ? t(ROLE_LABEL_KEYS[id]) : id);

const DETAIL_LEVEL_KEYS: Record<
  string,
  { label: string; description: string }
> = {
  brief: { label: 'brief', description: 'shortAnswersJustWhatYouNeed' },
  standard: { label: 'standard', description: 'someDetailButStillConcise' },
  'in-depth': { label: 'inDepth', description: 'moreDetailAndExplanation' },
};

export const detailLevelText = (
  t: TFunction<'pleri/onboarding'>,
  id: string,
): { label: string; description: string } => ({
  label: t(DETAIL_LEVEL_KEYS[id]?.label ?? id),
  description: t(DETAIL_LEVEL_KEYS[id]?.description ?? id),
});

const technicalLevelOptions = (t: TFunction<'pleri/onboarding'>) => [
  {
    id: 'less-technical',
    label: t('lessTechnical'),
    description: t('iKeepThingsHighLevel'),
  },
  {
    id: 'mixed',
    label: t('mixed'),
    description: t('iMixHighLevelExplanationsWithSome'),
  },
  {
    id: 'very-technical',
    label: t('veryTechnical'),
    description: t('iGoDeepIntoTechnicalDetailsWhen'),
  },
];

interface SubStepProps {
  subStep: OnboardingSubStepId;
  onNext: () => void;
  onBack: () => void;
  canGoBack: boolean;
}

const TypingText: React.FC<{
  text: string;
  delay?: number;
  speed?: number;
  className?: string;
}> = ({ text, delay = 0, speed = 30, className }) => {
  const [displayed, setDisplayed] = useState('');
  const [complete, setComplete] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const delayRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const startTyping = () => {
      let index = 0;
      intervalRef.current = setInterval(() => {
        if (index < text.length) {
          setDisplayed(text.slice(0, index + 1));
          index += 1;
        } else {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          setComplete(true);
        }
      }, speed);
    };

    if (delay > 0) {
      delayRef.current = setTimeout(startTyping, delay);
    } else {
      startTyping();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (delayRef.current) {
        clearTimeout(delayRef.current);
      }
    };
  }, [text, delay, speed]);

  return (
    <span className={className}>
      {displayed}
      {!complete && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{
            duration: 0.8,
            repeat: Infinity,
            repeatType: 'reverse',
          }}
          className="ml-1 inline-block h-5 w-0.5 bg-current"
        />
      )}
    </span>
  );
};

const NameSubStep: React.FC<SubStepProps> = ({ onNext, onBack, canGoBack }) => {
  const { t } = useTranslation('pleri/onboarding');
  const { data, updateData } = useOnboarding();
  const { data: userProfile } = useUserProfile();
  const { data: userMemory } = useMemory();
  const updateMemory = useUpdateMemory();
  const [name, setName] = useState(data.name || '');
  const [isValid, setIsValid] = useState(false);
  const [showLogo, setShowLogo] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Pre-populate name from user memory (nickName) or user profile (name) if available and not already set
  useEffect(() => {
    if (!data.name && !name) {
      // First check memory for nickName
      if (userMemory?.memory?.nickName) {
        setName(userMemory.memory.nickName);
      } else if (userProfile?.name) {
        // Fallback to profile name
        setName(userProfile.name);
      }
    }
  }, [userMemory?.memory?.nickName, userProfile?.name, data.name, name]);

  useEffect(() => {
    setIsValid(name.trim().length > 0);
  }, [name]);

  useEffect(() => {
    const logoTimer = setTimeout(() => setShowLogo(true), 200);
    const greetingTimer = setTimeout(() => setShowGreeting(true), 600);
    const questionTimer = setTimeout(() => setShowQuestion(true), 1600);
    const inputTimer = setTimeout(() => setShowInput(true), 2500);

    return () => {
      clearTimeout(logoTimer);
      clearTimeout(greetingTimer);
      clearTimeout(questionTimer);
      clearTimeout(inputTimer);
    };
  }, []);

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInput]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to go back
      if (e.key === 'Escape' && canGoBack) {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoBack, onBack]);

  const [isUpdating, setIsUpdating] = useState(false);

  const handleNext = async () => {
    if (isValid && !isUpdating) {
      setIsUpdating(true);
      try {
        const trimmedName = name.trim();
        // Store nickName in user memory
        await updateMemory.mutateAsync({
          memory: {
            ...userMemory?.memory,
            nickName: trimmedName,
          },
        });
        updateData({ name: trimmedName });
        onNext();
      } catch (error) {
        console.error('Failed to update nickName in memory:', error);
        // Continue anyway - don't block user progress
        updateData({ name: name.trim() });
        onNext();
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="mx-auto max-w-2xl px-8 py-8"
    >
      <div className="flex flex-col">
        {/* Logo - Left aligned */}
        <AnimatePresence>
          {showLogo && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{
                type: 'spring',
                stiffness: 200,
                damping: 20,
                duration: 0.5,
              }}
              className="mb-8"
            >
              <PleriLogo size={80} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Greeting - Left aligned */}
        <AnimatePresence>
          {showGreeting && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="mb-6"
            >
              <h2
                className="mb-4 text-3xl leading-tight font-semibold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Archivo, sans-serif' }}
              >
                <span className="inline-block text-left">
                  <TypingText text={t('letGetToKnowEachOther')} speed={40} />
                </span>
              </h2>
              <p
                className="text-base leading-relaxed text-gray-600 dark:text-gray-400"
                style={{ fontFamily: 'Archivo, sans-serif' }}
              >
                {t('hiIPleriIHelpOurTeam')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Question - Left aligned */}
        <AnimatePresence>
          {showQuestion && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="mb-6 text-lg font-medium text-gray-900 dark:text-gray-100"
              style={{ fontFamily: 'Archivo, sans-serif' }}
            >
              {t('whatShouldICallYou')}
            </motion.p>
          )}
        </AnimatePresence>

        {/* Input - Left aligned */}
        {showInput && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="mb-8"
          >
            <label
              htmlFor="name-input"
              className="mb-2 block text-sm font-medium text-gray-900 dark:text-gray-100"
              style={{ fontFamily: 'Archivo, sans-serif' }}
            >
              {t('yourName')}
            </label>
            <input
              id="name-input"
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isValid) {
                  handleNext();
                }
              }}
              placeholder={t('enterYourName')}
              className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 text-base transition-all focus:border-[#FF732E] focus:ring-2 focus:ring-[#FF732E]/20 focus:outline-none dark:border-gray-700"
              style={{ fontFamily: 'Archivo, sans-serif' }}
            />
            {name.trim().length > 0 && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1.5 text-sm text-gray-500 dark:text-gray-400"
              >
                {t('iPulledThisNameFromYourProfile')}
              </motion.p>
            )}
          </motion.div>
        )}

        {/* Buttons - Left aligned */}
        {showInput && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="flex items-center justify-between"
          >
            {canGoBack && (
              <button
                onClick={onBack}
                className="group flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-800"
                title={t('goBackEsc')}
              >
                <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
                {t('common:back')}
              </button>
            )}
            <div className={canGoBack ? 'ml-auto' : ''}>
              <button
                onClick={handleNext}
                disabled={!isValid || isUpdating}
                className="flex items-center gap-2 rounded-lg px-8 py-3 text-base font-medium text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  backgroundColor:
                    isValid && !isUpdating ? BRAND_COLORS.primary : '#ccc',
                }}
                onMouseEnter={(e) => {
                  if (isValid && !isUpdating) {
                    e.currentTarget.style.backgroundColor =
                      BRAND_COLORS.primaryHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (isValid && !isUpdating) {
                    e.currentTarget.style.backgroundColor =
                      BRAND_COLORS.primary;
                  }
                }}
              >
                {isUpdating ? t('updating') : t('continue')}
                {!isUpdating && <ArrowRight className="h-5 w-5" />}
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

const RoleSubStep: React.FC<SubStepProps> = ({ onNext, onBack, canGoBack }) => {
  const { t } = useTranslation('pleri/onboarding');
  const { data, updateData } = useOnboarding();
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState(data.role || '');
  const [otherRole, setOtherRole] = useState(data.roleOther || '');
  const [isValid, setIsValid] = useState(false);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to go back
      if (e.key === 'Escape' && canGoBack) {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoBack, onBack]);

  useEffect(() => {
    if (selectedRole === 'something-else') {
      setIsValid(otherRole.trim().length > 0);
    } else {
      setIsValid(selectedRole.length > 0);
    }
  }, [selectedRole, otherRole]);

  const [isUpdating, setIsUpdating] = useState(false);

  const handleNext = async () => {
    if (isValid && !isUpdating) {
      setIsUpdating(true);
      try {
        const roleValue =
          selectedRole === 'something-else' ? otherRole.trim() : selectedRole;
        const role =
          ROLE_OPTIONS.find((r) => r.id === selectedRole)?.label ?? roleValue;
        // Update with all accumulated data: nickName + role
        await updateUserMemory(
          {
            nickName: data.name,
            role,
            detailLevel: data.detailLevel,
            technicalLevel: data.technicalLevel,
          },
          queryClient,
        );
        updateData({
          role,
          roleOther:
            selectedRole === 'something-else' ? otherRole.trim() : undefined,
        });
        onNext();
      } catch (error) {
        console.error('Failed to update role:', error);
        // Continue anyway - don't block user progress
        updateData({
          role: selectedRole,
          roleOther:
            selectedRole === 'something-else' ? otherRole.trim() : undefined,
        });
        onNext();
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="mx-auto max-w-3xl px-8 py-12"
    >
      <h2
        className="mb-4 text-3xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100"
        style={{ fontFamily: 'Archivo, sans-serif' }}
      >
        {t('whatYourRole')}
      </h2>
      <p className="mb-8 text-base text-gray-600 dark:text-gray-400">
        {t('ifIKnowYourRoleICan')}
      </p>
      <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {ROLE_OPTIONS.map((role) => (
          <button
            key={role.id}
            onClick={() => {
              setSelectedRole(role.id);
              if (role.id !== 'something-else') {
                setOtherRole('');
              }
            }}
            className={`group relative flex items-center justify-between rounded-lg border-2 p-4 text-left transition-all ${
              selectedRole === role.id
                ? 'border-[#FF732E] bg-[#FF732E]/10'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800'
            }`}
          >
            <span
              className="text-sm font-medium text-gray-900 dark:text-gray-100"
              style={{ fontFamily: 'Archivo, sans-serif' }}
            >
              {roleLabel(t, role.id)}
            </span>
            {selectedRole === role.id && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="flex h-5 w-5 items-center justify-center rounded-full"
                style={{ backgroundColor: BRAND_COLORS.primary }}
              >
                <Check className="h-3 w-3 text-white" />
              </motion.div>
            )}
          </button>
        ))}
      </div>
      {selectedRole === 'something-else' && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-8"
        >
          <input
            type="text"
            value={otherRole}
            onChange={(e) => setOtherRole(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && isValid) {
                handleNext();
              }
            }}
            placeholder={t('describeYourRole')}
            className="w-full rounded-lg border-2 border-gray-200 px-6 py-4 text-lg transition-all focus:border-[#FF732E] focus:ring-2 focus:ring-[#FF732E]/20 focus:outline-none dark:border-gray-700"
            style={{ fontFamily: 'Archivo, sans-serif' }}
            autoFocus
          />
        </motion.div>
      )}
      <div className="flex items-center justify-between">
        {canGoBack && (
          <button
            onClick={onBack}
            className="group flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-800"
            title={t('goBackEsc')}
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            {t('common:back')}
          </button>
        )}
        <div className="ml-auto">
          <button
            onClick={handleNext}
            disabled={!isValid || isUpdating}
            className="flex items-center gap-2 rounded-lg px-8 py-3 text-base font-medium text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor:
                isValid && !isUpdating ? BRAND_COLORS.primary : '#ccc',
            }}
            onMouseEnter={(e) => {
              if (isValid && !isUpdating) {
                e.currentTarget.style.backgroundColor =
                  BRAND_COLORS.primaryHover;
              }
            }}
            onMouseLeave={(e) => {
              if (isValid && !isUpdating) {
                e.currentTarget.style.backgroundColor = BRAND_COLORS.primary;
              }
            }}
          >
            {isUpdating ? t('updating') : t('continue')}
            {!isUpdating && <ArrowRight className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const DetailLevelSubStep: React.FC<SubStepProps> = ({
  onNext,
  onBack,
  canGoBack,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const { data, updateData } = useOnboarding();
  const queryClient = useQueryClient();
  const [detailLevel, setDetailLevel] = useState(
    data.detailLevel || 'standard',
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to go back
      if (e.key === 'Escape' && canGoBack) {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoBack, onBack]);

  const [isUpdating, setIsUpdating] = useState(false);

  const handleNext = async () => {
    if (!isUpdating) {
      setIsUpdating(true);
      try {
        const level = detailLevel as 'brief' | 'standard' | 'in-depth';
        // Update with all accumulated data: nickName + role + detail level
        const roleValue =
          data.role === 'something-else' ? data.roleOther : data.role;
        const role =
          ROLE_OPTIONS.find((r) => r.id === roleValue)?.label ?? roleValue;
        await updateUserMemory(
          {
            nickName: data.name,
            role,
            detailLevel: level,
            technicalLevel: data.technicalLevel,
          },
          queryClient,
        );
        updateData({ detailLevel: level });
        onNext();
      } catch (error) {
        console.error('Failed to update detail level:', error);
        // Continue anyway - don't block user progress
        updateData({
          detailLevel: detailLevel as 'brief' | 'standard' | 'in-depth',
        });
        onNext();
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="mx-auto max-w-2xl px-8 py-12"
    >
      <h2
        className="mb-4 text-3xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100"
        style={{ fontFamily: 'Archivo, sans-serif' }}
      >
        {t('howDetailedShouldIBe')}
      </h2>
      <p className="mb-8 text-base text-gray-600 dark:text-gray-400">
        {t('iUseThisAsMyDefaultStyle')}
      </p>
      <div className="mb-8 space-y-2">
        {DETAIL_LEVEL_OPTIONS.map((option) => (
          <button
            key={option.id}
            onClick={() =>
              setDetailLevel(option.id as 'brief' | 'standard' | 'in-depth')
            }
            className={`group relative flex w-full items-start gap-3 rounded-lg border-2 p-4 text-left transition-all ${
              detailLevel === option.id
                ? 'border-[#FF732E] bg-[#FF732E]/10'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800'
            }`}
          >
            <div
              className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 ${
                detailLevel === option.id
                  ? 'border-[#FF732E] bg-[#FF732E]'
                  : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
              }`}
            >
              {detailLevel === option.id && (
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-white dark:bg-gray-900" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Archivo, sans-serif' }}
              >
                {detailLevelText(t, option.id).label}
              </div>
              <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                {detailLevelText(t, option.id).description}
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between">
        {canGoBack && (
          <button
            onClick={onBack}
            className="group flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-800"
            title={t('goBackEsc')}
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            {t('common:back')}
          </button>
        )}
        <div className="ml-auto">
          <button
            onClick={handleNext}
            disabled={isUpdating}
            className="flex items-center gap-2 rounded-lg px-8 py-3 text-base font-medium text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: isUpdating ? '#ccc' : BRAND_COLORS.primary,
            }}
          >
            {isUpdating ? t('updating') : t('continue')}
            {!isUpdating && <ArrowRight className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const TechnicalLevelSubStep: React.FC<SubStepProps> = ({
  onNext,
  onBack,
  canGoBack,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const { data, updateData } = useOnboarding();
  const queryClient = useQueryClient();
  const [technicalLevel, setTechnicalLevel] = useState(
    data.technicalLevel || 'mixed',
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to go back
      if (e.key === 'Escape' && canGoBack) {
        e.preventDefault();
        onBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoBack, onBack]);

  const [isUpdating, setIsUpdating] = useState(false);

  const handleNext = async () => {
    if (!isUpdating) {
      setIsUpdating(true);
      try {
        const level = technicalLevel as
          | 'less-technical'
          | 'mixed'
          | 'very-technical';
        // Update with all accumulated data: nickName + role + detail level + technical level
        const roleValue =
          data.role === 'something-else' ? data.roleOther : data.role;
        await updateUserMemory(
          {
            nickName: data.name,
            role: roleValue,
            detailLevel: data.detailLevel,
            technicalLevel: level,
          },
          queryClient,
        );
        updateData({ technicalLevel: level });
        onNext();
      } catch (error) {
        console.error('Failed to update technical level:', error);
        // Continue anyway - don't block user progress
        updateData({
          technicalLevel: technicalLevel as
            | 'less-technical'
            | 'mixed'
            | 'very-technical',
        });
        onNext();
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="mx-auto max-w-2xl px-8 py-12"
    >
      <h2
        className="mb-4 text-3xl leading-tight font-semibold tracking-tight text-gray-900 dark:text-gray-100"
        style={{ fontFamily: 'Archivo, sans-serif' }}
      >
        {t('howTechnicalDoYouWantMeTo')}
      </h2>
      <p className="mb-8 text-base text-gray-600 dark:text-gray-400">
        {t('iWillUseThisToTuneHow')}
      </p>
      <div className="mb-8 space-y-2">
        {technicalLevelOptions(t).map((option) => (
          <button
            key={option.id}
            onClick={() =>
              setTechnicalLevel(
                option.id as 'less-technical' | 'mixed' | 'very-technical',
              )
            }
            className={`group relative flex w-full items-start gap-3 rounded-lg border-2 p-4 text-left transition-all ${
              technicalLevel === option.id
                ? 'border-[#FF732E] bg-[#FF732E]/10'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800'
            }`}
          >
            <div
              className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded-full border-2 ${
                technicalLevel === option.id
                  ? 'border-[#FF732E] bg-[#FF732E]'
                  : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
              }`}
            >
              {technicalLevel === option.id && (
                <div className="flex h-full w-full items-center justify-center">
                  <div className="h-2 w-2 rounded-full bg-white dark:bg-gray-900" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div
                className="text-sm font-medium text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Archivo, sans-serif' }}
              >
                {option.label}
              </div>
              <div className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                {option.description}
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between">
        {canGoBack && (
          <button
            onClick={onBack}
            className="group flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-500 dark:hover:bg-gray-800"
            title={t('goBackEsc')}
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            {t('common:back')}
          </button>
        )}
        <div className="ml-auto">
          <button
            onClick={handleNext}
            disabled={isUpdating}
            className="flex items-center gap-2 rounded-lg px-8 py-3 text-base font-medium text-white transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
            style={{
              backgroundColor: isUpdating ? '#ccc' : BRAND_COLORS.primary,
            }}
          >
            {isUpdating ? t('updating') : t('continue')}
            {!isUpdating && <ArrowRight className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const AboutYouSubSteps: React.FC<{
  subStep: OnboardingSubStepId;
  onNext: () => void;
  onBack: () => void;
  canGoBack: boolean;
}> = ({ subStep, onNext, onBack, canGoBack }) => {
  switch (subStep) {
    case 'name':
      return (
        <NameSubStep
          subStep={subStep}
          onNext={onNext}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'role':
      return (
        <RoleSubStep
          subStep={subStep}
          onNext={onNext}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'detail-level':
      return (
        <DetailLevelSubStep
          subStep={subStep}
          onNext={onNext}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    case 'technical-level':
      return (
        <TechnicalLevelSubStep
          subStep={subStep}
          onNext={onNext}
          onBack={onBack}
          canGoBack={canGoBack}
        />
      );
    default:
      return null;
  }
};
